import { fabric } from 'fabric';

import { makeLine, makeRect, makeScrim, tag } from './objects';
import type { ArtboardSize } from './types';

/**
 * Shapes, dividers and scrims for the Elements panel. Everything is a stock
 * Fabric type (Rect, Ellipse, Polygon, Path, Line, Group) carrying a csShape
 * tag, so the inspector knows which controls apply (corner radius on rects,
 * sides on polygons, points on stars) and the headless renderer draws them
 * without knowing any of this exists.
 */
export type ShapeId =
    | 'rect'
    | 'rounded'
    | 'circle'
    | 'ellipse'
    | 'triangle'
    | 'polygon'
    | 'star'
    | 'burst'
    | 'frame'
    | 'line'
    | 'dashed'
    | 'arrow';

export type DividerId = 'rule' | 'double' | 'dot' | 'short';
export type ScrimId = 'scrim-bottom' | 'scrim-top' | 'scrim-left' | 'overlay' | 'vignette';

export const SHAPES: ShapeId[] = ['rect', 'rounded', 'circle', 'ellipse', 'triangle', 'polygon', 'star', 'burst', 'frame', 'line', 'dashed', 'arrow'];
export const DIVIDERS: DividerId[] = ['rule', 'double', 'dot', 'short'];
export const SCRIMS: ScrimId[] = ['scrim-bottom', 'scrim-top', 'scrim-left', 'overlay', 'vignette'];

export function regularPolygonPoints(sides: number, r: number): Array<{ x: number; y: number }> {
    const n = Math.max(3, Math.min(12, Math.round(sides)));
    return Array.from({ length: n }, (_, i) => {
        const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
        return { x: r + r * Math.cos(a), y: r + r * Math.sin(a) };
    });
}

export function starPoints(points: number, r: number, inner: number): Array<{ x: number; y: number }> {
    const n = Math.max(3, Math.min(24, Math.round(points)));
    const out: Array<{ x: number; y: number }> = [];
    for (let i = 0; i < n * 2; i++) {
        const rr = i % 2 === 0 ? r : r * inner;
        const a = -Math.PI / 2 + (i * Math.PI) / n;
        out.push({ x: r + rr * Math.cos(a), y: r + rr * Math.sin(a) });
    }
    return out;
}

function polygon(points: Array<{ x: number; y: number }>, x: number, y: number, fill: string): fabric.Polygon {
    return new fabric.Polygon(points, {
        left: x,
        top: y,
        fill,
        strokeUniform: true,
        originX: 'left',
        originY: 'top',
        objectCaching: false,
    } as any);
}

/**
 * Rebuild a polygon/star with new parameters, keeping its place, size and style.
 * Fabric polygons compute their box from their points at construction, so the
 * honest way to change the number of sides is a new polygon in the same spot.
 */
export function rebuildPolygon(
    obj: fabric.Polygon,
    params: { sides?: number; inner?: number },
): fabric.Polygon {
    const any = obj as any;
    const kind = any.csShape as 'polygon' | 'star' | 'burst' | 'triangle';
    const sides = params.sides ?? any.csSides ?? (kind === 'star' ? 5 : 6);
    const inner = params.inner ?? any.csInner ?? (kind === 'burst' ? 0.82 : 0.45);
    const r = 100;
    const pts = kind === 'star' || kind === 'burst' ? starPoints(sides, r, inner) : regularPolygonPoints(sides, r);
    const w = obj.getScaledWidth();
    const h = obj.getScaledHeight();
    const center = obj.getCenterPoint();
    const next = polygon(pts, 0, 0, obj.fill as string);
    const props = obj.toObject();
    delete props.points; delete props.width; delete props.height; delete props.left; delete props.top;
    delete props.scaleX; delete props.scaleY; delete props.pathOffset; delete props.type; delete props.version;
    next.set(props);
    next.set({
        scaleX: w / (next.width || 1),
        scaleY: h / (next.height || 1),
    });
    next.setPositionByOrigin(center, 'center', 'center');
    const nAny = next as any;
    nAny.csShape = kind;
    nAny.csSides = sides;
    nAny.csInner = kind === 'star' || kind === 'burst' ? inner : undefined;
    nAny.csId = any.csId;
    next.setCoords();
    return next;
}

/** A new shape centred on the artboard, sized to it. */
export function createShape(id: ShapeId, art: ArtboardSize, color: string): fabric.Object {
    const u = Math.min(art.w, art.h);
    const s = u * 0.3;
    const cx = art.w / 2;
    const cy = art.h / 2;
    let obj: fabric.Object;
    switch (id) {
        case 'rect':
            obj = makeRect({ x: cx - s * 0.6, y: cy - s * 0.4, w: s * 1.2, h: s * 0.8, fill: color, name: 'Rectangle' });
            break;
        case 'rounded':
            obj = makeRect({ x: cx - s * 0.6, y: cy - s * 0.4, w: s * 1.2, h: s * 0.8, fill: color, rx: s * 0.12, name: 'Rounded rectangle' });
            break;
        case 'frame':
            obj = makeRect({ x: cx - s * 0.6, y: cy - s * 0.75, w: s * 1.2, h: s * 1.5, fill: 'rgba(0,0,0,0)', stroke: color, strokeWidth: Math.max(2, u * 0.004), name: 'Frame' });
            (obj as any).csShape = 'frame';
            break;
        case 'circle':
            obj = new fabric.Circle({ left: cx - s / 2, top: cy - s / 2, radius: s / 2, fill: color, strokeUniform: true } as any);
            tag(obj, { role: 'shape', name: 'Circle' });
            (obj as any).csShape = 'circle';
            break;
        case 'ellipse':
            obj = new fabric.Ellipse({ left: cx - s * 0.7, top: cy - s * 0.42, rx: s * 0.7, ry: s * 0.42, fill: color, strokeUniform: true } as any);
            tag(obj, { role: 'shape', name: 'Ellipse' });
            (obj as any).csShape = 'ellipse';
            break;
        case 'triangle':
        case 'polygon': {
            const sides = id === 'triangle' ? 3 : 6;
            obj = polygon(regularPolygonPoints(sides, s / 2), cx - s / 2, cy - s / 2, color);
            tag(obj, { role: 'shape', name: id === 'triangle' ? 'Triangle' : 'Polygon' });
            (obj as any).csShape = 'polygon';
            (obj as any).csSides = sides;
            break;
        }
        case 'star':
        case 'burst': {
            const pts = id === 'star' ? starPoints(5, s / 2, 0.45) : starPoints(16, s / 2, 0.82);
            obj = polygon(pts, cx - s / 2, cy - s / 2, color);
            tag(obj, { role: 'shape', name: id === 'star' ? 'Star' : 'Badge' });
            (obj as any).csShape = id;
            (obj as any).csSides = id === 'star' ? 5 : 16;
            (obj as any).csInner = id === 'star' ? 0.45 : 0.82;
            break;
        }
        case 'line':
            obj = makeLine(cx - s * 0.8, cy, cx + s * 0.8, cy, { color, width: Math.max(2, u * 0.004), role: 'shape', name: 'Line' });
            break;
        case 'dashed':
            obj = makeLine(cx - s * 0.8, cy, cx + s * 0.8, cy, { color, width: Math.max(2, u * 0.004), dash: [u * 0.018, u * 0.012], role: 'shape', name: 'Dashed line' });
            break;
        case 'arrow':
        default: {
            const L = s * 1.6;
            const head = u * 0.03;
            obj = new fabric.Path(`M 0 ${head} L ${L} ${head} M ${L - head} 0 L ${L} ${head} L ${L - head} ${head * 2}`, {
                left: cx - L / 2,
                top: cy - head,
                fill: '',
                stroke: color,
                strokeWidth: Math.max(2, u * 0.004),
                strokeLineCap: 'round',
                strokeLineJoin: 'round',
                strokeUniform: true,
            } as any);
            tag(obj, { role: 'shape', name: 'Arrow' });
            (obj as any).csShape = 'arrow';
            break;
        }
    }
    return obj;
}

export function createDivider(id: DividerId, art: ArtboardSize, color: string): fabric.Object {
    const u = Math.min(art.w, art.h);
    const w = art.w * 0.5;
    const x = (art.w - w) / 2;
    const y = art.h / 2;
    const sw = Math.max(1.5, u * 0.0022);
    if (id === 'rule') return makeLine(x, y, x + w, y, { color, width: sw, name: 'Rule' });
    if (id === 'short') return makeLine(art.w / 2 - u * 0.04, y, art.w / 2 + u * 0.04, y, { color, width: sw * 1.6, name: 'Accent rule' });
    if (id === 'double') {
        const gap = u * 0.008;
        const g = new fabric.Group([
            makeLine(x, y, x + w, y, { color, width: sw }),
            makeLine(x, y + gap, x + w, y + gap, { color, width: sw }),
        ]);
        tag(g, { role: 'divider', name: 'Double rule' });
        return g;
    }
    const r = u * 0.006;
    const g = new fabric.Group([
        makeLine(x, y, art.w / 2 - r * 4, y, { color, width: sw }),
        new fabric.Circle({ left: art.w / 2 - r, top: y - r, radius: r, fill: color } as any),
        makeLine(art.w / 2 + r * 4, y, x + w, y, { color, width: sw }),
    ]);
    tag(g, { role: 'divider', name: 'Ornament rule' });
    return g;
}

export function createScrim(id: ScrimId, art: ArtboardSize): fabric.Object {
    if (id === 'scrim-bottom') return makeScrim('bottom', art.w, art.h, 0.65);
    if (id === 'scrim-top') return makeScrim('top', art.w, art.h, 0.55, '#000000', 0.45);
    if (id === 'scrim-left') return makeScrim('left', art.w, art.h, 0.6, '#000000', 0.65);
    if (id === 'overlay') return makeScrim('full', art.w, art.h, 0.55);
    const r = makeRect({
        x: 0,
        y: 0,
        w: art.w,
        h: art.h,
        role: 'scrim',
        name: 'Vignette',
        fill: new fabric.Gradient({
            type: 'radial',
            gradientUnits: 'percentage',
            coords: { x1: 0.5, y1: 0.5, r1: 0.25, x2: 0.5, y2: 0.5, r2: 0.75 },
            colorStops: [
                { offset: 0, color: 'rgba(0, 0, 0, 0)' },
                { offset: 1, color: 'rgba(0, 0, 0, 0.55)' },
            ],
        } as any),
    });
    return r;
}
